/**
 * ChatErrorCard — graceful error UI for an Ask Argus stream that failed
 * mid-turn (PR-AAU-C).
 *
 * Replaces the loading bubble (or in-place inside the assistant bubble)
 * when a `type: 'error'` SSE event arrives or the fetch itself fails.
 * Critically: it does NOT disable the composer — the user can keep
 * typing the next message while reviewing the error.
 *
 * Actions:
 *   - Retry         → re-sends the original prompt that produced this turn
 *   - Copy details  → clipboard the raw error payload as JSON
 */

import { useState } from 'react';
import { AlertTriangle, RotateCw, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ChatErrorState } from '@/lib/chat-stream-state';

interface ChatErrorCardProps {
  error: ChatErrorState;
  /** Optional prompt to re-send when the user clicks Retry. */
  retryPrompt?: string;
  onRetry?: (prompt: string) => void;
  className?: string;
}

/**
 * Map error codes to slightly friendlier plain-language descriptions.
 * Anything not in the map falls through to the raw `message`.
 */
const FRIENDLY_BY_CODE: Record<string, string> = {
  RATE_LIMITED:
    "You've sent more requests than the per-minute limit allows. Wait a moment and try again.",
  INTERNAL_ERROR:
    'Ask Argus hit an internal error. Retrying often clears it; if not, your admin can check the logs.',
  AI_UNAVAILABLE:
    'The AI engine is temporarily unavailable. Retry, or your admin can check the inference service.',
  // Foundations 1 / B0-T1. `FEATURE_DISABLED` went with the feature gate that
  // emitted it (decisions.md 2026-09-08); the two refusals that survived name
  // the switch that is off and the page it lives on, so the user knows what to
  // ask their admin for. The route's own message already says this — these are
  // the friendlier second voice for the card, and the fallback is that message.
  WEB_SEARCH_DISABLED:
    'Web search is turned off for your organization, so this turn cannot reach the public web. An administrator can allow it in Admin › Settings › General.',
  EXTERNAL_CONNECTIONS_DISABLED:
    'External connections are turned off for your organization, so this turn cannot reach connectors or MCP servers. An administrator can allow them in Admin › Settings › General.',
  UNAUTHORIZED: 'Your session expired. Sign in again to continue chatting.',
  INVALID_CONTEXT:
    'The request context was incomplete. Refreshing the page usually fixes this.',
};

export function ChatErrorCard({
  error,
  retryPrompt,
  onRetry,
  className,
}: ChatErrorCardProps) {
  const [copied, setCopied] = useState(false);

  const friendly = error.code ? FRIENDLY_BY_CODE[error.code] : undefined;
  const description = friendly ?? error.message;

  const handleCopy = async () => {
    try {
      const payload = JSON.stringify(
        error.raw ?? { code: error.code, message: error.message },
        null,
        2,
      );
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (insecure context). Ignore — the
      // retry button still works and the user can read the message.
    }
  };

  const canRetry = !!(onRetry && retryPrompt);

  return (
    <div
      className={cn(
        'rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5',
        className,
      )}
      data-testid="chat-error-card"
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-destructive">
            {error.code ?? 'Something went wrong'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 break-words">
            {description}
          </p>
          <div className="flex items-center gap-1.5 mt-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => canRetry && onRetry!(retryPrompt!)}
              disabled={!canRetry}
              data-testid="chat-error-card-retry"
            >
              <RotateCw className="h-3 w-3" />
              Retry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={handleCopy}
              data-testid="chat-error-card-copy"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy details'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
